import DeleteIcon from "@assets/icons/delete.svg";
import EyeVisibleIcon from "@assets/icons/eye-visible.svg";
import { useI18n } from "@core/i18n/i18n";
import { BaseExercise } from "@domain/exercise/exercise";
import { AnalyticsViewExerciseDetailOrigin } from "@domain/library/exercise-analytics";
import { useUser } from "@domain/user/hooks/use-user";
import styled from "@emotion/styled";
import { MenuButton, MenuPosition } from "@ui/components/common/menu-button";
import { useEditorExerciseCardActions } from "@ui/pages/program-builder/hooks/use-editor-exercise-card-actions";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";
import React, { useCallback, useMemo } from "react";

interface Props {
	alternativeExercise: BaseExercise;
	relatedExercises: BaseExercise[];
	onDelete?: (exercise: BaseExercise) => void;
}

export const AlternativeExerciseOptions: React.FC<Props> = ({ alternativeExercise, relatedExercises, onDelete }) => {
	const { format } = useI18n();
	const user = useUser();
	const { openExerciseDetailsModal } = useEditorExerciseCardActions();

	const viewDetails = useCallback(() => {
		isNotNil(user) &&
			openExerciseDetailsModal(
				alternativeExercise,
				relatedExercises,
				AnalyticsViewExerciseDetailOrigin.CANVAS_EXERCISE_DETAIL,
			);
	}, [alternativeExercise, openExerciseDetailsModal, relatedExercises, user]);

	const items = useMemo(
		() => [
			{
				text: format("programBuilder.exerciseCard.details"),
				icon: <EyeVisibleIcon />,
				onClick: viewDetails,
			},
			{
				text: format("programBuilder.exerciseCard.delete"),
				icon: <DeleteIcon />,
				onClick: () => onDelete?.(alternativeExercise),
				destructive: true,
			},
		],
		[alternativeExercise, format, onDelete, viewDetails],
	);

	return (
		<StyledMenuButton
			menuItems={items}
			menuId={`popup_menu_${alternativeExercise.draggableId}`}
			menuPosition={MenuPosition.CenteredPosition}
			menuIconSize={24}
		/>
	);
};

const StyledMenuButton = styled(MenuButton)`
	> button {
		padding: 0 2px;

		> div {
			background-color: unset;
			width: auto;
			height: auto;
			padding: 6px;

			:hover {
				background-color: ${colors.primaryBackground};
			}
		}
	}
`;
